// applications/_shared types — the dossier's step vocabulary.
//
// Lives in _shared (not dossier/types) because TWO features need it without reaching into each
// other: the dossier ROUTE needs `toDossierStep` to validate `?step=` in `validateSearch`, which
// runs during routing, before the screen component loads (reaching into the dossier feature's
// hooks/components barrels for a four-line string narrowing would pull React Query and the whole
// rendering tree onto first paint — same reasoning as the round builder's `wizard-step.ts`); the
// espace feature needs it to route "Reprendre mon dossier" at the right built step. Nothing here
// imports React or Zod, so either caller takes it for free.

/**
 * All four tabs the stepper shows, in the round's own order (Figma `426:15238` / `533:33654`).
 *
 * This is the FULL dossier shape — every round has these four sections, whether or not this build
 * has a screen for all of them yet. `BUILT_DOSSIER_STEPS` below is the narrower, growing list of
 * which ones actually have one.
 */
export const DOSSIER_STEPS = [
  "founder",
  "project",
  "team",
  "practical",
] as const;

export type DossierStep = (typeof DOSSIER_STEPS)[number];

/**
 * Which steps have real FIELD rendering — now all four, since Practical was reconnected onto the
 * foundation shell (2026-08-29). Kept as its own list rather than collapsed into `DOSSIER_STEPS`
 * because the question it answers is still real for anything future-built-ahead-of-its-fields
 * (matches the round builder's own `isOptionalStep`-style narrowing pattern) — it simply has no
 * gap to describe at the moment. Not the same question as "is this step reachable": the foundation
 * shell (`DossierFoundationScreen`) reaches all four from `DOSSIER_STEPS` with a placeholder body
 * regardless, since a step with no fields yet still has a screen — an empty one, deliberately.
 */
export const BUILT_DOSSIER_STEPS: readonly DossierStep[] = [
  "founder",
  "project",
  "team",
  "practical",
];

export function isBuiltDossierStep(
  step: DossierStep
): step is (typeof BUILT_DOSSIER_STEPS)[number] {
  return BUILT_DOSSIER_STEPS.includes(step);
}

/**
 * Narrow an unknown `?step=` value to one of the four real tabs; anything else falls back to the
 * first. Deliberately validates against ALL FOUR (`DOSSIER_STEPS`), not just `BUILT_DOSSIER_STEPS`
 * — the foundation shell gives every one of them a real, navigable screen (placeholder body where
 * fields aren't built yet), so `?step=practical` is a valid destination now, not a step to bounce
 * away from. `isBuiltDossierStep` stays the narrower "has real fields" question, for whatever still
 * needs to ask it (the espace card's "resume where there's something to actually do").
 */
export function toDossierStep(value: unknown): DossierStep {
  return DOSSIER_STEPS.find((step) => step === value) ?? "founder";
}

/**
 * Everything the dossier ROUTE can show — the four tabs plus `review`, mirroring the round
 * builder's own `BuilderView` (`rounds/configuration/types/wizard-step.ts`) for the identical
 * reason: `review` is a VIEW reached from the last tab's Next button, not a fifth tab in
 * `FoundationStepper` — it has its own header and layout entirely (US-E3-09/10/11/12), not the
 * 4-tab stepper chrome.
 */
export const DOSSIER_VIEWS = [...DOSSIER_STEPS, "review"] as const;

export type DossierView = (typeof DOSSIER_VIEWS)[number];

/** Narrow an unknown `?step=` value to a dossier view; anything else starts at the beginning. */
export function toDossierView(value: unknown): DossierView {
  return DOSSIER_VIEWS.find((view) => view === value) ?? "founder";
}

/** True when the view is one of the four wizard tabs rather than the review screen. */
export function isDossierStep(view: DossierView): view is DossierStep {
  return view !== "review";
}
